import {Box, Stack} from "@mui/material";
import {TeamProgressProvider, useTeamProgressContext} from "./context/TeamProgressContext";
import {ExportTeamProgressProvider} from "./context/ExportTeamProgressContext";
import {Filters} from "./components/Filters";
import {WidgetList} from "./components/Widgets";
import {LessonsCompletedChart} from "./components/Chart";
import {
	CompletedLessonsDataGrid,
	CompletedCoursesDataGrid,
	ActiveUsersDataGrid,
	InactiveUsersDataGrid,
	TimeInTrainingDataGrid,
	OverdueLessonsDataGrid
} from "./components/DataGrids";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useDownloadReport} from "./hooks";
import {AdminHeader} from "@/pages/ManagerView/AdminHeader";
import {useGetCohortSettings} from "@/app2/shared/data/repos/cohorts";
import {useOwnedCohortId} from "@/app2/shared/hooks/useCohort";

/**
 * Team Progress Page Content
 * 
 * Layout matches legacy AdminTeamProgressPage exactly:
 * - Header (left) with Filters (right)
 * - Widgets (left 50%) and Chart (right 50%) side-by-side
 * - Data Grid below (full width)
 */
function TeamProgressPageContent() {
	const {selectedTile} = useTeamProgressContext();
	const {t} = useSSTranslation();

	const cohortId = useOwnedCohortId();
	const {data: settings} = useGetCohortSettings(cohortId!, {
		enabled: !!cohortId
	});

	// Initialize WebSocket listener for export completion
	useDownloadReport();

	return (
		<Box sx={{display: "flex", justifyContent: "center"}}>
			<Stack sx={{maxWidth: "1444px", width: "100%"}} gap={2}>
				{/* Header with Filters */}
				<Stack
					direction="row"
					sx={{
						justifyContent: "space-between",
						alignItems: "flex-start"
					}}
				>
					<AdminHeader
						title={t("team_progress_title", "Team Progress")}
						subtitle={t("team_progress_subtitle", "Monitor learning engagement and employee progress")}
					/>
					<Filters />
				</Stack>

				{/* Widgets (left) and Chart (right) in 50/50 split */}
				<Stack
					gap={2}
					direction={{xs: "column-reverse", lg: "row"}}
					sx={{height: {lg: "448px"}}}
				>
					<Box sx={{width: {xs: "100%", lg: "50%"}}}>
						<WidgetList />
					</Box>
					<Box sx={{width: {xs: "100%", lg: "50%"}}}>
						<LessonsCompletedChart />
					</Box>
				</Stack>

				{/* Data Grid (conditionally rendered based on selected tile) */}
				<Box>
					{selectedTile === "completedLessons" && <CompletedLessonsDataGrid organization={settings} />}
					{selectedTile === "completedCourses" && <CompletedCoursesDataGrid organization={settings} />}
					{selectedTile === "activeUsers" && <ActiveUsersDataGrid organization={settings} />}
					{selectedTile === "inactiveUsers" && <InactiveUsersDataGrid organization={settings} />}
					{selectedTile === "timeInTraining" && <TimeInTrainingDataGrid organization={settings} />}
					{selectedTile === "usersWithOverdueLessons" && <OverdueLessonsDataGrid organization={settings} />}
				</Box>
			</Stack>
		</Box>
	);
}

/**
 * Team Progress Page
 * 
 * Manager dashboard for monitoring team learning progress.
 * Exact replication of legacy AdminTeamProgressPage layout and functionality.
 */
export function TeamProgressPage() {
	return (
		<ExportTeamProgressProvider>
			<TeamProgressProvider>
				<TeamProgressPageContent />
			</TeamProgressProvider>
		</ExportTeamProgressProvider>
	);
}
